import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Field, Button, Alert } from "@/components/ui";
import { Logo } from "@/components/Logo";

// Post-login destinations allowed via ?next=. Must be an exact, same-origin
// relative path. Rejects absolute URLs, scheme/protocol-relative (//), and
// backslashes to prevent open-redirect / phishing on this login page.
const ALLOWED_NEXT = new Set([
  "/onboarding", "/app", "/app/meds", "/app/calendar", "/app/vitals",
  "/app/scribe", "/app/claims", "/app/vault", "/app/inbox", "/app/patients",
  "/app/invite", "/app/billing", "/app/sessions", "/app/privacy", "/app/account",
]);

// Tokenised invite landings can also be a post-login destination: someone who
// already has an account has to sign in before they can join a care circle, and
// sending them back to a bare /app loses the invitation. Matched by shape, not
// by membership, since the token varies — still same-origin-relative only, and
// the token charset is url-safe base64 so nothing exotic can ride in.
const NEXT_TOKEN_PATHS = /^\/(family-invite|patient-invite)\/[A-Za-z0-9_-]{16,128}$/;

function safeNext(raw) {
  if (!raw || typeof raw !== "string") return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  if (/[\\]/.test(raw) || /[a-z][a-z0-9+.-]*:/i.test(raw) || /[\u0000-\u001f]/.test(raw)) return null;
  const path = raw.split(/[?#]/)[0];
  return ALLOWED_NEXT.has(path) || NEXT_TOKEN_PATHS.test(path) ? path : null;
}

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const { signIn, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState("demo@kinbridgehealth.com");
  const [password, setPassword] = useState("DemoPass123!");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await signIn(email.trim(), password);
      navigate(next || "/app", { replace: true });
    } catch (ex) {
      setErr(ex?.message || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between w-2/5 bg-brand-dark text-white p-12">
        <Logo className="h-9" onDark />
        <div>
          <h2 className="font-display text-4xl leading-tight">Welcome back to the circle.</h2>
          <p className="text-mint-tint mt-4 text-lg">
            Coordinate care, log meds, and fight overcharges — together.
          </p>
        </div>
        <p className="text-xs text-mint-tint/60">HIPAA-conscious care coordination</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm">
          <Logo className="h-9" testid="brand-name" />
          <h1 className="font-display text-3xl text-ink mt-4 mb-1">Sign in</h1>
          <p className="text-sm text-subtle mb-6">Your family&apos;s advocate inside the healthcare system.</p>

          <Field label="Email" type="email" data-testid="login-email-input" value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
          <Field label="Password" type="password" data-testid="login-password-input" value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" autoComplete="current-password" />
          <Alert testid="login-error">{err}</Alert>

          <Button type="submit" data-testid="login-submit-button" loading={busy} className="w-full mt-2">Sign in</Button>

          <div className="flex items-center my-5">
            <div className="flex-1 h-px bg-border" />
            <span className="px-3 text-xs text-subtle">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <button
            type="button"
            data-testid="login-google-button"
            onClick={() => { setErr(null); signInWithGoogle().catch((e) => setErr(e?.message || "Could not start Google sign-in")); }}
            className="w-full flex items-center justify-center gap-3 rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-medium text-ink hover:bg-gray-50 transition-colors"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <p className="text-sm text-subtle text-center mt-6">
            New here?{" "}
            <Link to="/signup" data-testid="go-to-signup-link" className="text-brand font-semibold hover:underline">Create an account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
